import {
  Button,
  TextField,
  Checkbox,
  AppHeader,
  Typography,
  Alert,
  useTextValidator,
  useZipCodeValidator,
  useStateInitialsValidator,
  Form,
} from '@ace-clublabs/shared-component-library';
import React, { useContext, useEffect, useState, useLayoutEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { Address } from '@lib/types';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { ConfirmZipCodeModal } from '../components/ConfirmZipCodeModal';
import { getNextPage } from '../utils/getNextPage';
import { UtagType } from '../utils/UtagHelper';
import { FlowType } from '@ace-clublabs/common-membership-api-types';
import { EmployeeInvalidClubModal } from '../components/EmployeeInvalidClubModal';
import { AddressCompareModal } from '../components/AddressCompareModal';
import { getProgressProps } from '../utils/helpers';
import { AppProgressHeader } from '../components/AppProgressHeader';
import { getAddressPageTitle } from '../utils/stringHelper';

export interface ICorrectedResidence {
  address?: Address;
}

interface IAddressPageProps {
  addressType: 'residence' | 'mailing';
}

export const AddressPage: React.FC<IAddressPageProps> = ({ addressType }) => {
  const {
    sessionData,
    submitMembershipForm,
    updateCache,
    loadingProvider,
    loadingInfo,
    membershipInfo,
    refreshSession,
    sendTag,
    updateMembershipForm,
    getNewBusinessCost,
    handleZipCodeChange,
  } = useContext(MembershipAppStateContext);

  const { formData, sessionId } = sessionData;
  const history = useHistory();
  const [isTagging, setIsTagging] = useState(true);
  const [serverValidationErrors, setServerValidationErrors] = useState({ hasError: false, error: '' });
  const [correctedResidence, setCorrectedResidence] = useState<ICorrectedResidence | null>(null);
  const [showAddressCorrectionModal, setShowAddressCorrectionModal] = useState(false);
  const [acceptedAddressChanges, setAcceptedAddressChanges] = useState(false);
  const [showConfirmZipCodeModal, setShowConfirmZipCodeModal] = useState(false);
  const [isOOT, setIsOOT] = useState(false);
  const isAddressCorrected = correctedResidence != null;
  const isGiftFlow = sessionData.flowType === FlowType.GIFT;
  const isGrouponFlow = sessionData.flowType === FlowType.GROUPON;

  const differentAddressText = isGiftFlow
    ? 'Recipient has a different mailing address'
    : 'I have a different mailing address';
  const residenceAddressTitle = isGiftFlow ? 'Recipient’s address' : 'Street address';
  const mailingAddressTitle = isGiftFlow ? 'Address' : 'Street address';
  // set and modified when mailing address checkbox is clicked so new form renders without internal state
  const [formKey, setFormKey] = useState(new Date().toISOString());
  const [showEmployeeInvalidClubModal, setEmployeeInvalidClubModal] = useState(false);

  const textValidator = useTextValidator();
  const zipCodeValidator = useZipCodeValidator();
  const stateValidator = useStateInitialsValidator();

  const currentPage = addressType === 'residence' ? Pages.ResidenceAddressPage : Pages.MailingAddressPage;
  const isGettingResidenceAddress = addressType === 'residence';

  const getPageNameTag = () => {
    if (isGiftFlow) {
      return `membership:gift:${isGettingResidenceAddress ? 'manual recipient address' : 'recipient mailing address'}`;
    } else {
      return isGettingResidenceAddress ? 'manual contact information' : 'mailing information';
    }
  };
  const pageNameTag = getPageNameTag();

  const getInitialAddressState = (address: Address | undefined): Address => {
    return {
      streetAddress: address?.streetAddress || '',
      city: address?.city || '',
      state: address?.state || '',
      zipCode: address?.zipCode?.slice(0, 5) || '',
    };
  };
  const tag = () => {
    sendTag(UtagType.view, pageNameTag);
    setIsTagging(false);
  };

  const [residenceAddressForm, setResidenceAddressFormData] = useState(
    getInitialAddressState(formData.residenceAddress)
  );
  const [hasMailingAddress, setHasMailingAddress] = useState(
    formData.mailingAddress != undefined &&
      formData.mailingAddress.streetAddress !== '' &&
      formData.mailingAddress.city !== '' &&
      formData.mailingAddress.state !== '' &&
      formData.mailingAddress.zipCode !== ''
  );
  const [mailingAddressForm, setMailingAddressFormData] = useState(getInitialAddressState(formData.mailingAddress));

  useEffect(() => {
    const updatedResidenceFormData = {
      ...sessionData.formData,
      residenceAddress: {
        ...sessionData.formData.residenceAddress,
        ...residenceAddressForm,
      },
    };
    updateMembershipForm(updatedResidenceFormData);
  }, [residenceAddressForm]);

  useEffect(() => {
    const updatedMailingAddressFormData = {
      ...sessionData.formData,
      mailingAddress: {
        ...sessionData.formData.mailingAddress,
        ...mailingAddressForm,
      },
    };
    updateMembershipForm(updatedMailingAddressFormData);
  }, [mailingAddressForm]);

  useEffect(() => {
    if (acceptedAddressChanges) {
      setAcceptedAddressChanges(false);
      submitForm({ skipApiAddressValidation: true });
    }
  }, [sessionData]);

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }

    tag();
  }, [membershipInfo]);

  const showOutOfTerritoryError = () => {
    sendTag(UtagType.link, pageNameTag, {
      error: `${isGiftFlow ? pageNameTag : `membership:join:${pageNameTag}`}-out of territory`,
    });
    setServerValidationErrors({
      hasError: true,
      error: `Your zip code is not part of this club's coverage area.`,
    });
  };

  const handleOutOfTerritoryError = (isOutOfACETerritory: boolean, zipCode: string) => {
    const isMajorRocket = sessionData.flowType === FlowType.MAJOR_ROCKET;

    if (isMajorRocket) {
      showOutOfTerritoryError();
    } else if (isGiftFlow) {
      history.push({
        pathname: Pages.GiftRecipientClubMismatchPage,
        state: { isOutOfACETerritory, zipCode },
      });
    } else {
      setIsOOT(isOutOfACETerritory);
      setShowConfirmZipCodeModal(true);
    }
    loadingProvider.endLoading();
  };

  const navigateToNextPage = async () => {
    loadingProvider.startLoading('Saving your membership information');
    await getNewBusinessCost();
    loadingProvider.endLoading();
    history.push(getNextPage(currentPage, sessionData.flowType));
  };

  const progressProps = getProgressProps(sessionData.flowType!, currentPage);

  const submitForm = async (props?: { skipApiAddressValidation?: boolean }) => {
    try {
      loadingProvider.startLoading('Saving your membership information');
      setServerValidationErrors({ hasError: false, error: '' });
      const response = await submitMembershipForm(props?.skipApiAddressValidation, true);
      const isOutOfACETerritory = response?.isOutOfACETerritory ?? false;
      const isEmployeeFlow = sessionData.flowType === FlowType.EMPLOYEE;
      if (response?.isActiveMember) {
        sendTag(UtagType.link, pageNameTag, {
          error: `${isGiftFlow ? pageNameTag : `membership:join:${pageNameTag}`}-current active member (VALD)`,
        });
        if (sessionData.flowType === FlowType.T_MOBILE) {
          history.push(Pages.MemberExists);
        } else if (sessionData.flowType === FlowType.GROUPON) {
          history.push(Pages.PartnerMemberExists);
        } else {
          setServerValidationErrors({
            hasError: true,
            error: 'An active Membership is associated with your name and address.',
          });
        }
        loadingProvider.endLoading();
      } else if (isEmployeeFlow && !response.isEmployeeClubValid) {
        setEmployeeInvalidClubModal(true);
        loadingProvider.endLoading();
      } else if (isGrouponFlow && !response.isGrouponClubValid) {
        history.push(Pages.OfferNotAvailable);
      } else if (isOutOfACETerritory || response?.isClubCodeMismatch) {
        //Check both OutOfTerritory && ClubCodeMismatch
        handleOutOfTerritoryError(isOutOfACETerritory, residenceAddressForm?.zipCode ?? '');
      } else if (response?.addressValidation?.status === 'CORRECTED') {
        const correction = {} as ICorrectedResidence;
        correction.address = {
          streetAddress: response.addressValidation.correctedAddress?.addressLine1 ?? '',
          city: response.addressValidation.correctedAddress?.city ?? '',
          state: response.addressValidation.correctedAddress?.state ?? '',
          zipCode: `${response.addressValidation.correctedAddress?.zip} - ${response.addressValidation.correctedAddress?.zipSuffix}`,
        };
        setCorrectedResidence(correction);
        setShowAddressCorrectionModal(true);
        loadingProvider.endLoading();
      } else if (response?.addressValidation?.status === 'INVALID') {
        setShowAddressCorrectionModal(true);
        loadingProvider.endLoading();
      } else if (hasMailingAddress && isGettingResidenceAddress) {
        history.push(Pages.MailingAddressPage);
      } else {
        navigateToNextPage();
      }
    } catch (e) {
      const isBotDetectionResponse =
        // @ts-ignore
        e?.error?.response?.status === 403 && !!e.error?.response?.data?.pxEvent;
      if (isBotDetectionResponse) {
        loadingProvider.endLoading();
        window._pxOnCaptchaSuccess = function (isValid) {
          if (isValid) {
            submitForm(props);
          }
        };
      } else {
        history.push(Pages.SorryPage);
      }
    }
  };

  const saveMailingAddress = async () => {
    await updateCache(sessionId || '', { mailingAddress: mailingAddressForm });
    navigateToNextPage();
  };

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return (
    <>
      <AppProgressHeader {...progressProps} />
      {loadingInfo.isLoading ? (
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={2} currentStep={loadingInfo.loadingDetails.length} />
      ) : (
        <>
          <EmployeeInvalidClubModal
            open={showEmployeeInvalidClubModal}
            onClose={() => {
              setEmployeeInvalidClubModal(false);
            }}
          />
          <div className="address-header-container">
            <Typography variant="h5" className="address-header-title">
              {getAddressPageTitle(isGiftFlow, isGettingResidenceAddress)}
            </Typography>
            {serverValidationErrors?.hasError ? (
              <Alert
                children={
                  <Typography className="validation-error-alert" variant="b3Regular">
                    {serverValidationErrors?.error}
                  </Typography>
                }
                severity="error"
                displayCloseButton={false}
              />
            ) : showAddressCorrectionModal ? (
              <AddressCompareModal
                isOpen={showAddressCorrectionModal}
                onClose={() => {
                  setCorrectedResidence(null);
                  setShowAddressCorrectionModal(false);
                }}
                isAddressCorrected={isAddressCorrected}
                addressForm={residenceAddressForm}
                correctedAddress={correctedResidence}
                acceptCorrectionClick={() => {
                  setResidenceAddressFormData((prev) => ({
                    ...prev,
                    streetAddress: correctedResidence?.address?.streetAddress ?? '',
                    city: correctedResidence?.address?.city ?? '',
                    state: correctedResidence?.address?.state ?? '',
                    zipCode: correctedResidence?.address?.zipCode ?? '',
                  }));

                  setCorrectedResidence(null);
                  setShowAddressCorrectionModal(false);
                  setAcceptedAddressChanges(true);
                }}
                navigateToNextPage={() => {
                  if (hasMailingAddress && isGettingResidenceAddress) {
                    history.push(Pages.MailingAddressPage);
                  } else {
                    navigateToNextPage();
                  }
                }}
              />
            ) : isGiftFlow ? (
              <></>
            ) : (
              <Typography variant="b1Regular" className="address-header-text">
                We need your address to validate your Auto Club membership.
              </Typography>
            )}
          </div>

          <Form key={formKey} sx={{ marginTop: 0 }}>
            <div className="address-form qm-mask">
              <TextField
                value={
                  isGettingResidenceAddress ? residenceAddressForm.streetAddress : mailingAddressForm.streetAddress
                }
                label={isGettingResidenceAddress ? residenceAddressTitle : mailingAddressTitle}
                className={isGettingResidenceAddress ? 'residence-street-address' : 'mailing-street-address'}
                id="streetAddress"
                name="streetAddress"
                fullWidth
                required
                data-qm-block="true"
                validator={textValidator}
                showClearButton={true}
                onChange={(v) => {
                  if (isGettingResidenceAddress) {
                    setResidenceAddressFormData((prev) => ({ ...prev, streetAddress: v.currentTarget.value }));
                  } else {
                    setMailingAddressFormData((prev) => ({ ...prev, streetAddress: v.currentTarget.value }));
                  }
                }}
                onClearButtonClick={() => {
                  if (isGettingResidenceAddress) {
                    setResidenceAddressFormData((prev) => ({ ...prev, streetAddress: '' }));
                  } else {
                    setMailingAddressFormData((prev) => ({ ...prev, streetAddress: '' }));
                  }
                }}
                sx={{
                  marginTop: '1rem',
                  '& .MuiFormLabel-asterisk': {
                    display: 'none',
                  },
                }}
              />
              <div className="city-state-container">
                <div className="address-page-small-input-box">
                  <TextField
                    value={isGettingResidenceAddress ? residenceAddressForm.city : mailingAddressForm.city}
                    label="City"
                    id="city"
                    name="city"
                    fullWidth
                    required
                    data-qm-block="true"
                    validator={textValidator}
                    showClearButton={true}
                    onChange={(v) => {
                      if (isGettingResidenceAddress) {
                        setResidenceAddressFormData((prev) => ({ ...prev, city: v.currentTarget.value }));
                      } else {
                        setMailingAddressFormData((prev) => ({ ...prev, city: v.currentTarget.value }));
                      }
                    }}
                    onClearButtonClick={() => {
                      if (isGettingResidenceAddress) {
                        setResidenceAddressFormData((prev) => ({ ...prev, city: '' }));
                      } else {
                        setMailingAddressFormData((prev) => ({ ...prev, city: '' }));
                      }
                    }}
                    sx={{
                      '& .MuiFormLabel-asterisk': {
                        display: 'none',
                      },
                    }}
                  />
                </div>
                <div className="address-page-small-input-box">
                  <TextField
                    value={isGettingResidenceAddress ? residenceAddressForm.state : mailingAddressForm.state}
                    label="State"
                    id="state"
                    name="state"
                    fullWidth
                    required
                    data-qm-block="true"
                    validator={stateValidator}
                    showClearButton={true}
                    onChange={(v) => {
                      if (isGettingResidenceAddress) {
                        setResidenceAddressFormData((prev) => ({
                          ...prev,
                          state: v.currentTarget.value.toUpperCase(),
                        }));
                      } else {
                        setMailingAddressFormData((prev) => ({
                          ...prev,
                          state: v.currentTarget.value.toUpperCase(),
                        }));
                      }
                    }}
                    onClearButtonClick={() => {
                      if (isGettingResidenceAddress) {
                        setResidenceAddressFormData((prev) => ({ ...prev, state: '' }));
                      } else {
                        setMailingAddressFormData((prev) => ({ ...prev, state: '' }));
                      }
                    }}
                    sx={{
                      '& .MuiFormLabel-asterisk': {
                        display: 'none',
                      },
                    }}
                  />
                </div>
              </div>
              <div className="address-page-small-input-box zip-input-container">
                <TextField
                  value={isGettingResidenceAddress ? residenceAddressForm.zipCode : mailingAddressForm.zipCode}
                  label="ZIP code"
                  id="zipCode"
                  name="zipCode"
                  fullWidth
                  required
                  data-qm-block="true"
                  validator={zipCodeValidator}
                  showClearButton={true}
                  onChange={(v) => {
                    if (isGettingResidenceAddress) {
                      setResidenceAddressFormData((prev) => ({ ...prev, zipCode: v.currentTarget.value }));
                    } else {
                      setMailingAddressFormData((prev) => ({ ...prev, zipCode: v.currentTarget.value }));
                    }
                  }}
                  onClearButtonClick={() => {
                    if (isGettingResidenceAddress) {
                      setResidenceAddressFormData((prev) => ({ ...prev, zipCode: '' }));
                    } else {
                      setMailingAddressFormData((prev) => ({ ...prev, zipCode: '' }));
                    }
                  }}
                  sx={{
                    '& .MuiFormLabel-asterisk': {
                      display: 'none',
                    },
                  }}
                />
              </div>
            </div>

            {isGettingResidenceAddress && (
              <div className="check-items" id="checkbox-align">
                <Checkbox
                  name="check-different-mailing"
                  label={differentAddressText}
                  id="check-different-mailing"
                  variant="text"
                  checked={hasMailingAddress}
                  onChange={(e) => {
                    setHasMailingAddress(e.checked);
                    setFormKey(new Date().toISOString());
                    if (!e.checked) {
                      setMailingAddressFormData(() => ({
                        streetAddress: '',
                        city: '',
                        state: '',
                        zipCode: '',
                      }));
                    }
                  }}
                  fullWidth
                ></Checkbox>
              </div>
            )}

            <div className="address-form-btn">
              <Button
                label="Next"
                fullWidth
                sx={{ marginTop: '1.5rem' }}
                onClick={() => {
                  isGettingResidenceAddress ? submitForm({ skipApiAddressValidation: false }) : saveMailingAddress();
                }}
                type="submit"
                variant="contained"
                size="medium"
                id="nextButton"
              ></Button>
            </div>
            <ConfirmZipCodeModal
              isOpen={showConfirmZipCodeModal}
              closeModal={() => {
                showOutOfTerritoryError();
                setShowConfirmZipCodeModal(false);
              }}
              confirm={() => {
                handleZipCodeChange(isOOT);
                setShowConfirmZipCodeModal(false);
              }}
            />
          </Form>
        </>
      )}
    </>
  );
};
